import { QUESTION, SURVEY } from '@constants/terminology';
import type { BulkActionToolbarSelectedUtrv } from '@features/survey-question-list/partials/BulkActionToolbar';
import type { SurveyActionData } from '@g17eco/types/surveyAction';
import { Button } from 'reactstrap';
import { useBulkVerifyUtrvsMutation } from '@api/survey-utrvs';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import type { CurrentUserData } from '@reducers/current-user';
import { UtrvStatus } from '@constants/status';
import { UtrvPermissions } from '@services/permissions/UtrvPermissions';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

const MAX_BULK_ACTION_METRICS = 500;

interface BulkVerifyFilterResult {
  questionsToVerify: BulkActionToolbarSelectedUtrv[];
  mismatchCount: {
    cannotBeVerified: number;
    verificationOptional: number;
    userCannotVerify: number;
  };
}

const getDefaultFilterResult = (): BulkVerifyFilterResult => ({
  questionsToVerify: [],
  mismatchCount: {
    cannotBeVerified: 0,
    verificationOptional: 0,
    userCannotVerify: 0,
  },
});

const getTooltip = ({
  isBulkVerifyEnabled,
  exceedsMetricLimit,
  mismatchCount,
}: {
  isBulkVerifyEnabled: boolean;
  exceedsMetricLimit: boolean;
  mismatchCount?: BulkVerifyFilterResult['mismatchCount'];
}): string | undefined => {
  if (!isBulkVerifyEnabled) {
    return `Bulk verification is not enabled for this ${SURVEY.SINGULAR}`;
  }

  if (exceedsMetricLimit) {
    return `You have selected more than the allowed limit of ${MAX_BULK_ACTION_METRICS} ${QUESTION.PLURAL}. Please deselect some ${QUESTION.PLURAL} to continue`;
  }

  const messages = [];

  if (mismatchCount?.userCannotVerify) {
    messages.push(`${mismatchCount.userCannotVerify} ${QUESTION.SINGULAR}(s) due to missing permission`);
  }

  if (mismatchCount?.cannotBeVerified) {
    messages.push(`${mismatchCount.cannotBeVerified} ${QUESTION.SINGULAR}(s) that are unanswered or verified already`);
  }

  if (messages.length > 0) {
    return `Cannot verify ${messages.join(', ')}`;
  }
  return undefined;
};

export interface BulkVerifyButtonProps {
  user: CurrentUserData;
  survey: Pick<
    SurveyActionData,
    '_id' | 'roles' | 'stakeholders' | 'initiativeId' | 'verificationRequired' | 'bulkVerifyEnabled' | 'workgroups'
  >;
  selectedQuestions: BulkActionToolbarSelectedUtrv[];
  handleReload: () => Promise<void | undefined>;
}

export const BulkVerifyButton = (props: BulkVerifyButtonProps) => {
  const { user, survey, selectedQuestions, handleReload } = props;

  const [bulkVerifyUtrvs, { isLoading }] = useBulkVerifyUtrvsMutation();

  const isBulkVerifyEnabled = survey.bulkVerifyEnabled ?? false;

  const exceedsMetricLimit = selectedQuestions.length > MAX_BULK_ACTION_METRICS;

  const defaultFilterResult = getDefaultFilterResult();

  const { questionsToVerify, mismatchCount } =
    isBulkVerifyEnabled && !exceedsMetricLimit
      ? selectedQuestions.reduce<BulkVerifyFilterResult>((acc, question) => {
          if (![UtrvStatus.Updated, UtrvStatus.Rejected].includes(question.status as UtrvStatus)) {
            acc.mismatchCount.cannotBeVerified += 1;
            return acc;
          }

          if (!UtrvPermissions.canVerify(question, survey, user)) {
            acc.mismatchCount.userCannotVerify += 1;
            return acc;
          }
          // question can be verified and user has permission to verify
          acc.questionsToVerify.push(question);
          return acc;
        }, defaultFilterResult)
      : defaultFilterResult;

  const isDisabled = questionsToVerify.length === 0 || isLoading;

  const handleUpdate = async () => {
    if (isDisabled) {
      return;
    }
    try {
      const utrvIds = questionsToVerify.map((question) => question._id);

      const { data: result } = await bulkVerifyUtrvs({ surveyId: survey._id, utrvIds }).unwrap();

      const invalidMessages = [];

      if (result.errored > 0) {
        invalidMessages.push(`${result.errored} ${QUESTION.SINGULAR}(s) failed to verify`);
      }
      if (result.skipped > 0) {
        invalidMessages.push(`${result.skipped} ${QUESTION.SINGULAR}(s) were skipped`);
      }

      if (invalidMessages.length > 0) {
        generateToast({
          color: 'warning',
          title: `Partially verified ${QUESTION.PLURAL} successfully`,
          message: `${result.verified} ${QUESTION.SINGULAR}(s) verified, ${invalidMessages.join(', ')}`,
        });
      } else {
        generateToast({
          color: 'success',
          title: `Verified ${QUESTION.PLURAL} successfully`,
          message: `${result.verified} ${QUESTION.SINGULAR}(s) verified`,
        });
      }
      handleReload();
    } catch (error) {
      toastFromError({ error, title: `Failed to verify ${QUESTION.PLURAL}` });
    }
  };

  return (
    <SimpleTooltip
      text={getTooltip({
        isBulkVerifyEnabled,
        exceedsMetricLimit,
        ...(questionsToVerify.length === 0 ? { mismatchCount } : {}),
      })}
    >
      <Button onClick={handleUpdate} disabled={isDisabled}>
        <i className={'fal fa-check mr-2'} />
        Verify {QUESTION.PLURAL}
      </Button>
    </SimpleTooltip>
  );
};
